import React, {useState, useEffect, useCallback} from 'react';
import {motion, AnimatePresence} from 'motion/react';
import Overlay from './overlay';
import {SequentialModalContext} from './context';
import {sequentialStorage} from '@utils/Storage/SequentialStorage';

export type SequentialTransitionType =
  | 'slide'
  | 'fade'
  | 'scale'
  | 'flip'
  | 'zoom-in-out';

interface SequentialModalItemProps {
  sequentialConsume?: boolean;
  sequentialOverlayClassName?: string;
  sequentialTransitionDuration?: number;
  sequentialTransitionType?: SequentialTransitionType;
}

interface SequentialModalProps {
  modals: React.ReactNode[];
  visible: boolean;
  onAllClosed?: () => void;
  className?: string;
  overlayClassName?: string;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
  animationType?: 'fade' | 'slide' | 'zoom' | 'none';
  backdropBlur?: boolean;
  position?: 'center' | 'top' | 'bottom';
  allowPrevious?: boolean;
  // 新增转场动画配置
  transitionType?: SequentialTransitionType;
  transitionDuration?: number;
}
const SequentialModal: React.FC<SequentialModalProps> = ({
  modals,
  visible,
  onAllClosed,
  className = '',
  overlayClassName = '',
  closeOnClickOutside = false,
  closeOnEscape = true,
  animationType = 'fade',
  backdropBlur = false,
  position = 'center',
  allowPrevious = false,
  transitionType = 'slide',
  transitionDuration = 0.3,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isClosing, setIsClosing] = useState(false); // 新增：标记是否正在关闭
  const currentModal = modals[currentIndex];
  const currentModalProps = React.isValidElement(currentModal)
    ? (currentModal.props as SequentialModalItemProps)
    : undefined;
  const currentTransitionType =
    currentModalProps?.sequentialTransitionType || transitionType;
  const currentTransitionDuration =
    currentModalProps?.sequentialTransitionDuration || transitionDuration;
  const currentOverlayClassName =
    currentModalProps?.sequentialOverlayClassName || overlayClassName;

  // 动画变体配置
  const getAnimationVariants = () => {
    switch (currentTransitionType) {
      case 'slide':
        return {
          enter: (direction: number) => ({
            x: direction > 0 ? 300 : -300,
            opacity: 0,
          }),
          center: {
            x: 0,
            opacity: 1,
          },
          exit: (direction: number) => ({
            x: direction < 0 ? 300 : -300,
            opacity: 0,
          }),
        };

      case 'fade':
        return {
          enter: {
            opacity: 0,
          },
          center: {
            opacity: 1,
          },
          exit: {
            opacity: 0,
          },
        };

      case 'scale':
        return {
          enter: {
            scale: 0.8,
            opacity: 0,
          },
          center: {
            scale: 1,
            opacity: 1,
          },
          exit: {
            scale: 1.1,
            opacity: 0,
          },
        };

      case 'zoom-in-out':
        return {
          enter: {
            scale: 0.6,
            opacity: 0,
          },
          center: {
            scale: 1,
            opacity: 1,
          },
          exit: {
            scale: 0.6,
            opacity: 0,
          },
        };

      case 'flip':
        return {
          enter: {
            rotateY: 90,
            opacity: 0,
          },
          center: {
            rotateY: 0,
            opacity: 1,
          },
          exit: {
            rotateY: -90,
            opacity: 0,
          },
        };

      default:
        return {
          enter: {opacity: 0},
          center: {opacity: 1},
          exit: {opacity: 0},
        };
    }
  };

  const variants = getAnimationVariants();

  // 当外部控制显示状态时，重置索引
  useEffect(() => {
    if (visible && modals.length > 0) {
      setCurrentIndex(0);
      setIsVisible(true);
      setIsClosing(false); // 重置关闭状态
    } else if (!visible) {
      setIsVisible(false);
      setCurrentIndex(0);
      setIsClosing(false);
    }
  }, [visible, modals.length]);

  // 关闭全部
  const closeAll = useCallback(() => {
    setIsClosing(true);
    setIsTransitioning(true);
    const cur: any = modals[currentIndex];
    // 消费一次
    if (cur.key && cur.props?.sequentialConsume !== false) {
      sequentialStorage.consumerSequential(cur.key);
    }

    // 等待动画完成后再隐藏组件
    setTimeout(() => {
      setIsVisible(false);
      setCurrentIndex(0);
      setIsClosing(false);
      setIsTransitioning(false);
      onAllClosed?.();
    }, currentTransitionDuration * 1000);
  }, [currentIndex, currentTransitionDuration, modals, onAllClosed]);

  // 关闭当前弹窗并显示下一个
  const closeCurrentModal = useCallback(async () => {
    const cur: any = modals[currentIndex];

    const nextIndex = currentIndex + 1;

    if (nextIndex < modals.length) {
      // 设置转场状态
      setIsTransitioning(true);
      // 消费一次
      if (cur.key && cur.props?.sequentialConsume !== false) {
        sequentialStorage.consumerSequential(cur.key);
      }
      // 等待动画完成后切换到下一个弹窗
      setTimeout(() => {
        setCurrentIndex(nextIndex);
        setIsTransitioning(false);
      }, currentTransitionDuration * 1000);
    } else {
      // 所有弹窗都已显示完毕，关闭最后一个弹窗时也要有动画
      closeAll();
    }
  }, [modals, currentIndex, currentTransitionDuration, closeAll]);

  // 跳转到下一个弹窗
  const goToNext = useCallback(() => {
    if (!isTransitioning) {
      closeCurrentModal();
    }
  }, [closeCurrentModal, isTransitioning]);

  // 跳转到上一个弹窗
  const goToPrevious = useCallback(() => {
    if (allowPrevious && currentIndex > 0 && !isTransitioning) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex(currentIndex - 1);
        setIsTransitioning(false);
      }, currentTransitionDuration * 1000);
    }
  }, [allowPrevious, currentIndex, isTransitioning, currentTransitionDuration]);

  // 跳转到指定索引的弹窗
  const goToIndex = useCallback(
    (index: number) => {
      if (index >= 0 && index < modals.length && !isTransitioning) {
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentIndex(index);
          setIsTransitioning(false);
        }, currentTransitionDuration * 1000);
      }
    },
    [modals.length, isTransitioning, currentTransitionDuration],
  );

  // 处理遮罩层关闭事件
  const handleOverlayClose = useCallback(() => {
    if ((closeOnClickOutside || closeOnEscape) && !isTransitioning) {
      closeCurrentModal();
    }
  }, [closeOnClickOutside, closeOnEscape, closeCurrentModal, isTransitioning]);

  // 如果没有弹窗或不可见，不渲染任何内容
  if (!isVisible || modals.length === 0) {
    return null;
  }

  // Context 的值
  const contextValue = {
    onClose: closeAll,
    modalIndex: currentIndex,
    totalModals: modals.length,
    isLastModal: currentIndex === modals.length - 1,
    isFirstModal: currentIndex === 0,
    goToNext,
    goToPrevious,
    goToIndex,
    isTransitioning, // 添加转场状态到 context
  };

  return (
    <SequentialModalContext.Provider value={contextValue}>
      <Overlay
        visible={isVisible}
        onClose={handleOverlayClose}
        className={className}
        overlayClassName={currentOverlayClassName}
        closeOnClickOutside={closeOnClickOutside}
        closeOnEscape={closeOnEscape}
        animationType={animationType}
        backdropBlur={backdropBlur}
        position={position}>
        <AnimatePresence
          mode="wait"
          custom={1}
          onExitComplete={() => {
            // 当退出动画完成且正在关闭时，确保清理状态
            if (isClosing) {
              // 这里可以添加额外的清理逻辑
            }
          }}>
          {/* 只有在不是正在关闭状态，或者是正在关闭但仍需要显示当前弹窗时才渲染 */}
          {(!isClosing || currentIndex < modals.length) && (
            <motion.div
              key={isClosing ? 'closing' : currentIndex}
              custom={1}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                duration: currentTransitionDuration,
                ease:
                  currentTransitionType === 'zoom-in-out'
                    ? [0.4, 0, 0.2, 1]
                    : 'easeInOut',
              }}
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems:
                  position === 'center'
                    ? 'center'
                    : position === 'top'
                    ? 'flex-start'
                    : 'flex-end',
                justifyContent: 'center',
              }}>
              {modals[currentIndex]}
            </motion.div>
          )}
        </AnimatePresence>
      </Overlay>
    </SequentialModalContext.Provider>
  );
};

export default SequentialModal;
